import { useFetcher } from "react-router-dom";

import noChartDataIllustration from "assets/images/dashboard-no-chart-data-illustration.png";

import Button from "components/Button";
import Card from "components/Card";
import ProgressBar from "components/ProgressBar";
import { PureTooltip } from "components/Tooltip";
import Spinner from "components/Spinner";

import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";

import UsageHistoryCharts from "pages/dashboard/UsageHistoryCharts";

import { UserType } from "types/Users";

import { Fetchers } from "routes/dataroutes/Fetchers";
import { useUser } from "routes/dataroutes/UserData";

import { fmtDate } from "utils";
import { dateDiff } from "utils/dateUtils";
import { DAY } from "utils/timeConstants";

import ActiveSubscription from "types/ActiveSubscription";
import DomainMultipliersText from "components/common/DomainMultipliers";


const getRemainingDays = (user: UserType | null, subscription: ActiveSubscription | null | undefined) => {
  if (user?.isFreeTrial) {
    const diff = dateDiff(new Date(user.signedUpAt!), Date.now(), DAY);
    return {
      action: "Ends",
      remainingDays: Math.max(0, 7 - diff)
    };
  }

  const remainingDays = subscription ? dateDiff(Date.now(), subscription.nextRenewalAt * 1000, DAY) : undefined;

  if (subscription?.scheduledSubscription) {
    return {
      action: subscription.price > subscription.scheduledSubscription.price ? "Downgrades" : "Upgrades",
      remainingDays: remainingDays
    };
  }

  return {
    action: "Renews",
    remainingDays: remainingDays
  };
};

const getTooltipContent = (user: UserType | null, subscription: ActiveSubscription | null | undefined) => {
  if (user?.isFreeTrial) {
    return "Your trial ends on " + fmtDate(
      new Date(new Date(user.signedUpAt!).getTime() + (7 * DAY)), { weekday: undefined }
    ) + ".";
  }

  if (!subscription) {
    return undefined;
  }

  let action = "Resets";

  if (subscription.scheduledSubscription) {
    action = (subscription.price > subscription.scheduledSubscription.price ? "Downgrades" : "Upgrades") + " to " + subscription.scheduledSubscription.planName;
  }

  return action + " on " + fmtDate(new Date(subscription.nextRenewalAt * 1000), { weekday: undefined });
}

function PlanEndsIn(
  {
    activeSubscription
  }: {
    activeSubscription: ActiveSubscription | null | undefined
  }
) {
  const user = useUser();

  const { action, remainingDays } = getRemainingDays(user, activeSubscription);
  const tooltipContent = getTooltipContent(user, activeSubscription);

  return (
    <PureTooltip content={ tooltipContent }>
      <div
        className="flex flex-row gap-x-1 items-center text-neutral-700 text-sm border-b-[1px] border-b-neutral-700 border-dotted cursor-default">
        { remainingDays === 0 && <span>{ action } today</span> }
        { remainingDays !== 0 && (
          <>
            <span>{ action } in</span>
            { remainingDays ?? <Spinner theme="secondary" size="XXS"/> }
            <span>{ (remainingDays || 2) > 1 ? "days" : "day" }</span>
          </>
        ) }
      </div>
    </PureTooltip>
  );
}

export function PlanEndsInLabel() {

  const { activeSubscription } = useActiveSubscription();

  return <PlanEndsIn activeSubscription={ activeSubscription } />;
}

export interface CountersData {
  requestCount: number;
  concurrentRequests: number;
  /**
   * same as API credit limit for the current plan
   */
  totalCredits: number;
  concurrencyLimit: number;
  payAsYouGoCredits?: number;
  payAsYouGoCreditsCap?: number | null;
  parsersUsed?: number;
}

function PlanUsageCard() {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const { pricingOptions } = usePricingOptions();

  // no need to do anything with this fetcher here, it's the usage charts component that will initiate the fetch
  // and which is also responsible for refreshing the data. here we just show what is fetched
  const chartsDataFetcher = useFetcher(Fetchers.CHARTS_DATA);
  const counters = chartsDataFetcher.data?.counters;

  const payAsYouGoCredits = counters?.payAsYouGoCredits ?? 0;
  const monthlyCreditsUsed = counters?.requestCount ?? 0;
  const monthlyCreditsLimit = counters?.totalCredits;
  const concurrentRequests = counters?.concurrentRequests ?? 0;
  const concurrencyLimit = counters?.concurrencyLimit;

  // Handle both positive values and negative values (which represent overage)
  const absolutePaygCredits = Math.abs(payAsYouGoCredits);

  // Only show PAYG as stacked value if plan allows it and there are PAYG credits (positive or negative)
  const paygStackedValue = pricingOptions?.["pay-as-you-go"]?.allowed && absolutePaygCredits > 0
    ? absolutePaygCredits
    : undefined;

  // Tooltip content for PAYG overage segment only
  const paygCreditsTooltip = paygStackedValue
    ? `Pay-as-you-go overage: ${absolutePaygCredits.toLocaleString()} credits`
    : undefined;

  return (
    <Card
      title={ user?.isFreeTrial ? "Trial" : (activeSubscription?.planName + " plan") }
      extra={ <PlanEndsInLabel /> }
      size="LG"
      borderless
      footer={ <Button text="Upgrade to larger plan" className="button button-primary w-full" href={ BillingPath } /> }
    >
      <div className="flex flex-col h-full py-4 gap-y-4">
        <ProgressBar
          label="API credits used"
          fullWidth
          current={ monthlyCreditsUsed }
          max={ monthlyCreditsLimit }
          stackedValue={ paygStackedValue }
          stackedTooltip={ paygCreditsTooltip }
          loading={ chartsDataFetcher.state === "loading" }
        />
        <ProgressBar
          label="Concurrent threads"
          fullWidth
          current={ concurrentRequests }
          max={ concurrencyLimit }
          loading={ chartsDataFetcher.state === "loading" }
        />
      </div>
    </Card>
  );
}

function NoUsagePanel() {
  return (
    <div className="flex flex-col gap-y-5 items-center self-center justify-self-center max-w-[274px]">
      <img src={ noChartDataIllustration } alt="there is no data yet" />
      <div className="flex flex-col gap-y-1.5 items-center">
        <p className="font-medium">There is no data yet!</p>
        <p className="text-sm text-center">Let’s get started and make your first request to see your statistics.</p>
      </div>
    </div>
  );
}

function ChartsPanel() {
  const user = useUser();

  return user?.madeFirstRequest ?
    <UsageHistoryCharts/> :
    <NoUsagePanel/>;
}

export default function PlanOverviewAndStatsCard() {
  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-row justify-between items-center flex-wrap">
      <div className="font-semibold text-neutral-900">Your plan</div>
      <DomainMultipliersText />
      </div>
      <Card size="LG">
        <div className="flex flex-col _1320:grid _1320:grid-cols-[330px_minmax(0,1fr)] gap-x-8 gap-y-12 h-full w-full">
          <PlanUsageCard />
          <ChartsPanel />
        </div>
      </Card>
    </div>
  );
};
